import { forwardRef, lazy, Suspense } from 'react'

// Lazy load the entire CodeMirror setup including extensions and theme
const CodeMirrorEditor = lazy(() =>
  Promise.all([
    import('@uiw/react-codemirror'),
    import('@codemirror/lang-html'),
    import('@uiw/codemirror-theme-monokai'),
    import('@codemirror/view')
  ]).then(([codeMirrorModule, htmlModule, monokaiModule, viewModule]) => {
    const CodeMirror = codeMirrorModule.default
    const html = htmlModule.html
    const monokai = monokaiModule.monokai
    const ariaLabel = viewModule.EditorView.contentAttributes.of({
      'aria-label': 'HTML source code'
    })

    return {
      default: forwardRef<
        any,
        { value: string; onChange: (val: string) => void }
      >(({ value, onChange }, ref) => (
        <CodeMirror
          ref={ref}
          value={value}
          height="700px"
          theme={monokai}
          extensions={[html(), ariaLabel]}
          onChange={onChange}
          basicSetup={{
            lineNumbers: true,
            highlightActiveLineGutter: true,
            highlightSpecialChars: true,
            foldGutter: true,
            drawSelection: true,
            dropCursor: true,
            allowMultipleSelections: true,
            indentOnInput: true,
            bracketMatching: true,
            closeBrackets: true,
            autocompletion: true,
            rectangularSelection: true,
            crosshairCursor: true,
            highlightActiveLine: true,
            highlightSelectionMatches: true,
            closeBracketsKeymap: true,
            searchKeymap: true,
            foldKeymap: true,
            completionKeymap: true,
            lintKeymap: true
          }}
        />
      ))
    }
  })
)

interface CodeEditorProps {
  value: string
  onChange: (value: string) => void
}

export const CodeEditor = forwardRef<any, CodeEditorProps>(
  ({ value, onChange }, ref) => {
    return (
      <div className="flex-1 min-w-0">
        <Suspense
          fallback={
            <div className="h-[700px] bg-gray-900 rounded border border-gray-700 flex items-center justify-center">
              <p className="text-gray-400">Loading code view...</p>
            </div>
          }
        >
          <CodeMirrorEditor ref={ref} value={value} onChange={onChange} />
        </Suspense>
      </div>
    )
  }
)

CodeEditor.displayName = 'CodeEditor'
